/**
 * Date/Time Display and Input Components
 *
 * ShelfCycle Standard: Org timezone should ALWAYS drive display, not browser local time.
 *
 * Key components:
 * - DateDisplay / DateTimeDisplay: Use `useOrgTimezone` prop to display in org timezone
 * - DateInput: When using with API queries, store picker value separately from API value
 *   to prevent day drift (see docs/TIMEZONE_STANDARDS.md for the correct pattern)
 * - WeekendWarning: Automatically uses org timezone when `weekendWarning` is enabled
 *
 * This `DateInput` is the OPINIONATED one (SC-2699): it normalises what it emits to browser-local
 * midnight, and adds a value format, a multi-format parser, a portalled dropdown, a deselect guard
 * and the weekend warning. The `DateInput` exported from ~/ui — which this one composes — is the
 * vendor-faithful facade and emits exactly what Mantine emits. New call sites take the ~/ui one
 * unless they want this policy. Changing either emit shifts accounting dates silently, so the two
 * stay separate deliberately.
 *
 * See docs/TIMEZONE_STANDARDS.md for comprehensive timezone handling documentation.
 *
 * @module datetime
 */

import dayjs from "dayjs";
import CustomParseFormat from "dayjs/plugin/customParseFormat";
import { DateTime } from "luxon";
import { ChangeEvent } from "react";

import { useOptionalOrg } from "~/contexts/orgProvider";
import {
  DateInput as _DateInput,
  type DateInputProps as _DateInputProps,
  TimeInput as _TimeInput,
  type TimeInputProps as _TimeInputProps,
  Flex,
  Input,
  Text,
  Tooltip,
} from "~/ui";
import {
  formatDateAsDate,
  formatDateAsDateTime,
  formatDateAsTime,
  formatTimeStringAsTime,
  humanReadableTimeDifference,
} from "~/utils/formatters";
import { isWeekend, warningColor, weekendWarningDisplay } from "~/utils/types/date";

import styles from "./datetime.module.css";

dayjs.extend(CustomParseFormat);

type DisplayProps = {
  value?: Date | null;
  nullValue?: string | undefined;
  weekendWarning?: boolean;
  span?: boolean;
  color?: string;
  useOrgTimezone?: boolean;
};

export const WeekendWarning = ({ date, timezone }: { date?: Date | null; timezone?: string }) => {
  if (!date || !isWeekend(date, timezone)) {
    return null;
  }
  return <Input.Error style={{ color: warningColor }}>{weekendWarningDisplay(date, timezone)}</Input.Error>;
};

export const DateDisplay = ({ value, weekendWarning, color, useOrgTimezone, ...props }: DisplayProps) => {
  const orgContext = useOptionalOrg();
  const args = {
    ...props,
    nullValue: props.nullValue ?? "",
  };
  // ShelfCycle Standard: Use org timezone for weekend detection when useOrgTimezone is enabled
  const timezone = useOrgTimezone ? (orgContext?.orgTimezone ?? undefined) : undefined;
  const showWarning = weekendWarning === true && isWeekend(value, timezone);
  return (
    <>
      <Text span={props.span} inherit color={color}>
        {value == null ? args.nullValue : formatDateAsDate(value, timezone)}
      </Text>
      {showWarning && <WeekendWarning date={value} timezone={timezone} />}
    </>
  );
};

export const TimeDisplay = ({ value, ...props }: DisplayProps) => {
  const args = {
    ...props,
    nullValue: props.nullValue ?? "",
  };
  return <>{value == null ? args.nullValue : formatDateAsTime(value)}</>;
};

type TimeStringDisplayProps = Omit<DisplayProps, "value"> & { value: string | null | undefined };
export const TimeStringDisplay = ({ value, color, ...props }: TimeStringDisplayProps) => {
  const args = {
    ...props,
    nullValue: props.nullValue ?? "",
  };
  return (
    <Text span={props.span} color={color}>
      {value == null ? args.nullValue : formatTimeStringAsTime(value)}
    </Text>
  );
};

export const DateTimeDisplay = ({ value, weekendWarning, useOrgTimezone, ...props }: DisplayProps) => {
  const orgContext = useOptionalOrg();
  const args = {
    ...props,
    nullValue: props.nullValue ?? "",
  };
  // ShelfCycle Standard: Use org timezone for weekend detection
  const timezone = useOrgTimezone ? (orgContext?.orgTimezone ?? undefined) : undefined;
  const warnWeekend = weekendWarning === true && isWeekend(value, timezone);

  return (
    <Flex direction="column" gap={0}>
      <Text span={props.span} inherit>
        {value == null ? args.nullValue : formatDateAsDateTime(value, timezone)}
      </Text>
      {warnWeekend && <Input.Error style={{ color: warningColor }}>{weekendWarningDisplay(value, timezone)}</Input.Error>}
    </Flex>
  );
};

export const DateInputValueFormat = "MMMM D, YYYY";
export const DateTimePickerValueFormat = DateInputValueFormat + " HH:mm";
const dateParser = (s: string) => {
  const dateFormats = [
    "M/d/yyyy",
    "M/d/yy",
    "M/d",
    "MMddyyyy",
    "MMddyy",
    "MMdd",
    "yyyy-MM-dd",
    "MMM d yyyy",
    "MMM d yy",
    "MMMM d yyyy",
    "MMMM d yy",
  ];
  for (const fmt of dateFormats) {
    const d = DateTime.fromFormat(s.replace(",", ""), fmt);
    if (d.isValid) {
      return d.startOf("day").toJSDate();
    }
  }
  return null;
};

type DateInputProps = _DateInputProps & {
  weekendWarning?: boolean;
};
export const DateInput = ({ weekendWarning, onChange, className, style, ...props }: DateInputProps) => {
  const orgContext = useOptionalOrg();
  // ShelfCycle Standard: When weekendWarning is enabled, automatically use org timezone
  // for weekend detection. This ensures the warning reflects the org's business context.
  const timezone = weekendWarning ? (orgContext?.orgTimezone ?? undefined) : undefined;
  const warnWeekend = weekendWarning === true && isWeekend(props.value, timezone);

  // Mantine DateInput behavior: when `clearable` is enabled, clicking the currently-selected
  // date in the calendar clears the input. This is easy to do accidentally (especially when
  // the user has typed a date, then clicks the same date to "confirm" it).
  //
  // We default to *not* allowing deselect via calendar click, while still supporting explicit
  // opt-in (`allowDeselect`) and the clear button from `clearable`.
  const allowDeselect = props.allowDeselect ?? (props.clearable ? false : undefined);

  const handleChange = (value: Date | null) => {
    if (value == null) {
      onChange?.(null);
      return;
    }
    onChange?.(DateTime.fromJSDate(value).startOf("day").toJSDate());
  };

  return (
    <div>
      <_DateInput
        valueFormat={DateInputValueFormat}
        dateParser={dateParser}
        popoverProps={{ withinPortal: true }}
        {...props}
        className={[className, warnWeekend && style === undefined ? styles.weekendWarning : ""].filter(Boolean).join(" ")}
        style={style}
        onChange={handleChange}
        allowDeselect={allowDeselect}
      />
      {!props.error && warnWeekend && (
        <Input.Error style={{ color: warningColor }}>{weekendWarningDisplay(props.value, timezone)}</Input.Error>
      )}
    </div>
  );
};

type TimeInputProps = Omit<_TimeInputProps, "onChange" | "value" | "defaultValue"> & {
  onChange?: (value: string | null) => void;
  value?: string | undefined | null;
  defaultValue?: string | undefined | null;
};
export const TimeInput = (props: TimeInputProps) => {
  const { value, defaultValue, onChange, ...rest } = props;
  const values = {
    value: value == null ? undefined : dayjs(value, "HH:mm").format("HH:mm"),
    defaultValue: defaultValue == null ? undefined : dayjs(defaultValue, "HH:mm").format("HH:mm"),
  };
  const _onChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length === 0) {
      onChange?.(null);
    } else {
      const rv = (e.target.value ?? "").length === 0 ? null : e.target.value + ":00";
      onChange?.(rv);
    }
  };
  return <_TimeInput {...rest} {...values} onChange={_onChange} />;
};

export const RelativeDateTimeDisplay = ({ value, span }: { value?: Date | null; span?: boolean }) => {
  if (!value) {
    return <></>;
  }
  const diff = humanReadableTimeDifference(value, { maxGranularity: "hour" });
  return (
    <Tooltip label={formatDateAsDateTime(value)} position="top" withArrow>
      <Text
        style={{
          whiteSpace: "nowrap",
          textDecoration: "underline",
          textDecorationStyle: "dotted",
          textDecorationColor: "currentColor",
        }}
        span={span}
      >
        {diff} ago
      </Text>
    </Tooltip>
  );
};

export const DateDisplayWithDateTimeHover = ({
  value,
  span,
  useOrgTimezone,
}: {
  value?: Date | null;
  span?: boolean;
  useOrgTimezone?: boolean;
}) => {
  const orgContext = useOptionalOrg();
  if (!value) {
    return <></>;
  }
  const timezone = useOrgTimezone ? (orgContext?.orgTimezone ?? undefined) : undefined;
  return (
    <Tooltip label={formatDateAsDateTime(value, timezone)} position="top" withArrow>
      <Text
        span={span}
        style={{
          textDecoration: "underline",
          textDecorationStyle: "dotted",
          textDecorationColor: "currentColor",
        }}
      >
        {formatDateAsDate(value, timezone)}
      </Text>
    </Tooltip>
  );
};

type TimeRangeDisplayProps = {
  label?: string;
  start?: string | null;
  end?: string | null;
};
export const TimeRangeDisplay = ({ label, start, end }: TimeRangeDisplayProps) => {
  if (start == null && end == null) {
    return <></>;
  }
  if (start != null && end != null && !dayjs(start, "HH:mm").isSame(dayjs(end, "HH:mm"))) {
    return (
      <Flex direction="row" gap="xs">
        {!!label && <Text>{label}</Text>}
        <TimeStringDisplay span value={start} />
        <Text>-</Text>
        <TimeStringDisplay span value={end} />
      </Flex>
    );
  }
  const value = start != null ? start : end;
  return (
    <Flex direction="row" gap="xs">
      {!!label && <Text>{label}</Text>}
      <TimeStringDisplay span value={value} />
    </Flex>
  );
};
